<template>
  <div class="sidebar" :class="{ open: appStore.sidebarIsOpen, mobile: isMobile }" @mouseenter="appStore.toggleSidebar(true)" @mouseleave="appStore.toggleSidebar(false)">
    <div class="sidebar-header">
      <LogoSimple />
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="!isOrganisationPending" class="sidebar-body" :key="organisation.id">
        <div v-if="canWriteCampaignAndSpaces" id="newCampaign" class="items-container">
          <Item :label="$t('app.sidebar.newCampaign')" icon="bi bi-pencil-square" :routeParams="{ name: 'newCampaign' }" :animationIsFinished="state.animationIsFinished" :sidebarIsOpen="appStore.sidebarIsOpen" data-step="2" />
        </div>
        <div class="items-container">
          <small class="menu-title">{{ $t("app.sidebar.menu") }}</small>
          <Item v-for="(item, index) in items" :key="index" :label="item.label" :icon="item.icon" :routeParams="item.route" :animationIsFinished="state.animationIsFinished" :sidebarIsOpen="appStore.sidebarIsOpen" :data-step="item.dataStep" />
        </div>
        <div v-if="!isSpacesPending" class="items-container">
          <small class="menu-title">{{ $t("app.sidebar.spaces") }}</small>
          <Item v-if="canWriteCampaignAndSpaces" :label="$t('app.sidebar.manageSpaces')" icon="bi bi-folder" :routeParams="{ name: 'spaces' }" :animationIsFinished="state.animationIsFinished" :sidebarIsOpen="appStore.sidebarIsOpen" data-step="4" />
          <div class="divider" />
          <ItemFetch data-step="7" v-for="(item, index) in sortedSpaces" :key="index" :space="item" :animationIsFinished="state.animationIsFinished" :sidebarIsOpen="appStore.sidebarIsOpen" />
        </div>
      </div>
    </transition>
    <div class="sidebar-body-addons">
      <div class="items-container" data-step="5">
        <small class="menu-title">{{ $t("app.sidebar.credits") }}</small>
        <div class="credit-wrapper">
          <div class="credits-container">
            <div class="tag red" v-text="labelCredit" />
            <img src="/images/defaults/space/lemon-yellow.svg" alt="Credits" />
          </div>
          <div v-if="!isCreditsPending" class="credits-info">
            <div class="total" v-text="credits.available" />
            {{ $t("app.sidebar.remainingCredits", { count: credits.available }) }}
          </div>
        </div>
      </div>
    </div>
    <Footer :class="{ open: appStore.sidebarFooterIsOpen || isMobile }" @mouseenter="!isMobile ? appStore.toggleSidebarFooter(true) : null" @mouseleave="!isMobile ? appStore.toggleSidebarFooter(false) : null" :animationIsFinished="state.animationIsFinished" :sidebarIsOpen="appStore.sidebarIsOpen" data-step="6" />
  </div>
</template>

<script setup lang="ts">
import { reactive, watch, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import Footer from "./Footer.vue";
import Item from "./Item.vue";
import ItemFetch from "./ItemFetch.vue";

import { useAppStore } from "@/stores/app.js";
import { useOrganisation, useOrganisationCredits } from "@/queries/organisation.query";
import { useSpaces } from "@/queries/space.query";

import { usePermissions } from "@/composables/permissions";

const { t } = useI18n();

const { canWriteCampaignAndSpaces, canManageOrganisation, isPending: isRolesPending } = usePermissions();
const { data: organisation, isPending: isOrganisationPending } = useOrganisation();
const { data: credits, isPending: isCreditsPending } = useOrganisationCredits();
const { data: spaces, isPending: isSpacesPending } = useSpaces();

const appStore = useAppStore();

defineProps({
  isMobile: {
    type: Boolean,
    required: true,
  },
});

const state = reactive({
  animationIsFinished: false,
});

const labelCredit = computed(() => {
  if (isCreditsPending.value) return null;

  return credits.value.available > 999 ? `${Math.floor(credits.value.available / 1000)}K+` : credits.value.available;
});

const sortedSpaces = computed(() => {
  if (isSpacesPending.value) return [];
  
  return [...spaces.value].sort((a, b) => a.name.localeCompare(b.name));
});

const items = computed(() => {
  if (isOrganisationPending.value || isSpacesPending.value || isRolesPending.value) return [];

  let homeRoute;
  if (canWriteCampaignAndSpaces.value) {
    homeRoute = {
      name: "home",
      params: {
        companyId: organisation.value.id,
      },
    }
  } else {
    if (spaces.value.length > 0) {
      homeRoute = {
        name: "space",
        params: {
          companyId: organisation.value.id,
          spaceId: spaces.value[0].workspace_id,
        }
      }
    } else {
      homeRoute = {
        name: "spaces",
        params: {
          companyId: organisation.value.id,
        }
      }
    }
  }

  const items_list: any[] = [
    {
      label: t("app.home.label"),
      icon: "bi bi-house",
      route: homeRoute,
    },
  ];

  if (canManageOrganisation.value) {
    items_list.push({
      label: t("app.company.label"),
      icon: "bi bi-wallet2",
      route: {
        name: "company",
      },
      dataStep: "3",
    });
  }

  return items_list;
});

watch(
  () => appStore.sidebarIsOpen,
  (isOpen) => {
    if (!isOpen) {
      state.animationIsFinished = false;
    } else {
      setTimeout(() => {
        state.animationIsFinished = true;
      }, 150);
    }
  },
);

onMounted(() => {
  appStore.sidebarIsMounted = true;
  state.animationIsFinished = true;
});
</script>
